import classNames from 'classnames';
import { useAppSelector } from 'hooks/redux';
import React, { memo, useState, useMemo, useCallback } from 'react';

import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { makeStyles } from 'tss-react/mui';

import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { taxFormsSelectors } from 'app-components/flux/tax-forms';
import { taxLineItemsSelectors } from 'app-components/flux/tax-line-items';

import DataGridStyle1 from 'components/DataGridStyle1';
import Dump from 'components/Dump';
import QButton from 'components/QButton';
import SearchBox from 'components/SearchBox';

import SettingsHeader from 'containers/Settings/SharedComponents/SettingsHeader';
import CategoryMenu from './CategoryMenu';
import UpsertCategoryDialog from './UpsertCategoryDialog';
import { initialCaps } from './utils';

const useStyles = makeStyles()((theme) => ({
  groupColHeader: {
    ...theme.typography.body1,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `2px solid ${theme.palette.border.light}`,
  },
  row: {
    '&:hover .CategoryButton': {
      opacity: 1,
    },
  },
  searchBoxOpen: {
    width: '200px !important',
  },
  usageTypeRow: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 500,
  },
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
  },
}));

function searchValueToRegex(value) {
  // escape any special characters
  let result = value.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

  // replace whitespace with regex whitespace
  result = result.replace(/\s\s+/g, '\\s');

  // replace * with .*
  result = result.replace(/\*/g, '.*');

  // replace ? with .
  result = result.replace(/\?/g, '.');

  // build regular expresion from resulting string and include options
  return new RegExp(result, 'gi');
}

const getName = (row) => row.name;

function CategoriesSettings() {
  const { classes } = useStyles();

  const [showUpsertCategoryDialog, setShowUpsertCategoryDialog] = useState(undefined);

  const [searchValue, setSearchValue] = useState(null);

  const enableNewCategorySettings = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'newCategorySettingsEnabled'),
  );

  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const categoriesByParentId = useAppSelector(categoriesSelectors.getCategoriesByParentId);
  const taxItemsEnabled = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'taxItemsEnabled'));

  const taxFormsById = useAppSelector(taxFormsSelectors.getResourcesById);
  const taxLineItemsById = useAppSelector(taxLineItemsSelectors.getResourcesById);

  const enableUsageTypes = useAppSelector(categoriesSelectors.enableCategoryUsageTypes);
  const l1CategoriesByUsageType = useAppSelector(categoriesSelectors.getL1CategoriesByUsageType);

  const apiRef = useGridApiRef();

  const getCategoryForId = useCallback((id) => categoriesById.get(id), [categoriesById]);

  const buildRowData = useCallback(
    (category, path) => {
      let rowsCalc = [];
      const rowId = `${category.name}-${category.id || category.clientId}`;
      let hasChildRows = false;
      let children = categoriesByParentId.get(category.id)?.toList();
      if (children) {
        children = children.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
        );
        children.forEach((child) => {
          const childRows = buildRowData(child, [...path, rowId]);
          if (childRows.length) {
            hasChildRows = true;
            rowsCalc = rowsCalc.concat(childRows);
          }
        });
      }

      // Tactical fix to support 3 level categories - we should make this recursive to support N level categories
      // however because of lingering issues with circular category references, this tactical approach is safer until that issue is fully resolved.
      const parent = getCategoryForId(category.parentId);
      const grandParent = getCategoryForId(parent?.parentId);

      if (
        hasChildRows ||
        !searchValue ||
        category.name.match(searchValue) ||
        parent?.name?.match(searchValue) ||
        grandParent?.name?.match(searchValue)
      ) {
        const automationId = `${path?.length > 0 ? `${path[path.length - 1]}-` : ''}${category?.name}`
          .toLowerCase()
          .replaceAll?.(' ', '-');
        rowsCalc.push({
          id: category.id || category.clientId,
          name: [...path, rowId],
          description: category.description,
          automationId,
          type: category.type,
        });
      }

      return rowsCalc;
    },
    [categoriesByParentId, getCategoryForId, searchValue],
  );

  const rows = useMemo(() => {
    let rowsCalc = [];
    if (enableUsageTypes) {
      l1CategoriesByUsageType.forEach((l1Categories, usageType) => {
        const name = `${usageType.charAt(0).toUpperCase() + usageType.slice(1).toLowerCase()} Categories`;
        rowsCalc.push({
          id: usageType,
          name: [name],
          usageTypeRow: true,
        });
        l1Categories
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
          .forEach((category) => {
            rowsCalc = rowsCalc.concat(buildRowData(category, [name], rowsCalc));
          });
      });
    } else {
      l1CategoriesByUsageType
        .values()
        .next()
        .value?.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }))
        .forEach((category) => {
          rowsCalc = rowsCalc.concat(buildRowData(category, [], rowsCalc));
        });
    }
    return rowsCalc;
  }, [buildRowData, enableUsageTypes, l1CategoriesByUsageType]);

  const renderExclusions = useCallback(
    (id) => {
      const category = getCategoryForId(id);
      if (category) {
        let exclusionTitle = null;
        if (category.isExcludedFromBudgets && category.isExcludedFromReports) {
          exclusionTitle = 'Exclude from Reports and Spending Plan';
        } else if (category.isExcludedFromBudgets) {
          exclusionTitle = 'Exclude from Spending Plan';
        } else if (category.isExcludedFromReports) {
          exclusionTitle = 'Exclude from Reports';
        }
        if (exclusionTitle) {
          return (
            <Tooltip title={exclusionTitle}>
              <VisibilityOffIcon color='disabled' fontSize='small' />
            </Tooltip>
          );
        }
      }
      return category && (category.isExcludedFromBudgets || category.isExcludedFromReports);
    },
    [getCategoryForId],
  );

  const renderTaxDescription = useCallback(
    (categoryId) => {
      const render = (taxForm, taxLineItem) => {
        return taxForm ? (
          <Tooltip title={taxLineItem?.description}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {!taxLineItem ? taxForm.name : `${taxForm.name}: ${taxLineItem.name}`}
            </span>
          </Tooltip>
        ) : (
          <></>
        );
      };

      const category = getCategoryForId(categoryId);
      if (!category) return '';

      let taxForm, taxLineItem;

      if (category.usageType === 'BUSINESS' && category.txfIds) {
        const taxLineItems = Object.values(category.txfIds);
        if (taxLineItems.length === 1) {
          taxLineItem = taxLineItemsById.get(taxLineItems[0]);
          taxForm = taxLineItem && taxFormsById.get(taxLineItem.formId);
          return render(taxForm, taxLineItem);
        } else {
          const taxFormsString = taxLineItems
            .map((taxLineItem) => {
              taxLineItem = taxLineItemsById.get(taxLineItem);
              taxForm = taxLineItem && taxFormsById.get(taxLineItem.formId);
              return taxForm?.name;
            })
            .filter((value) => value !== undefined)
            .join(', ');
          return <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{taxFormsString}</span>;
        }
      }

      if (category.usageType === 'PERSONAL' && category.txfId) {
        taxLineItem = taxLineItemsById?.get(category.txfId);
        taxForm = taxLineItem && taxFormsById.get(taxLineItem?.formId);
        return render(taxForm, taxLineItem);
      }
    },
    [getCategoryForId, taxFormsById, taxLineItemsById],
  );

  const commonColumnDef = useMemo(
    () => ({
      disableReorder: true,
      filterable: false,
      headerClassName: classes.groupColHeader,
      hideable: false,
      sortable: false,
    }),
    [classes.groupColHeader],
  );

  const columns = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Type',
        ...commonColumnDef,
        headerClassName: classNames(classes.groupColHeader, 'disable-blue-border'),
        resizable: false,
        width: 100,
        renderCell: (params) => {
          const category = getCategoryForId(params.id);
          return (
            <Box position='relative'>
              {initialCaps(params.formattedValue)}
              <Dump obj={category} />
            </Box>
          );
        },
      },
      ...(taxItemsEnabled
        ? [
            {
              field: 'taxLineItem',
              headerName: 'Tax Form & Line Item',
              ...commonColumnDef,
              flex: 1,
              renderCell: (params) => renderTaxDescription(params.id),
            },
          ]
        : []),
      ...(enableNewCategorySettings
        ? [
            {
              field: 'exclude',
              headerName: '',
              ...commonColumnDef,
              align: 'center',
              description: 'Exclude from Reports and/or Spending Plan',
              editable: false,
              headerClassName: classNames(classes.groupColHeader, 'disable-blue-border'),
              resizable: false,
              width: 50,
              renderCell: (params) => renderExclusions(params.id),
            },
          ]
        : []),
      {
        field: 'action',
        headerName: '',
        ...commonColumnDef,
        headerClassName: classNames(classes.groupColHeader, 'disable-blue-border'),
        resizable: false,
        renderCell: (params) => {
          const category = getCategoryForId(params.id);
          return category ? (
            <CategoryMenu
              key={params.id}
              category={category}
              automationId={params.row.automationId}
              enableNewCategorySettings={enableNewCategorySettings}
              enableTaxItems={taxItemsEnabled}
              enableUsageTypes={enableUsageTypes}
            />
          ) : null;
        },
        width: 65,
      },
    ],
    [
      classes.groupColHeader,
      commonColumnDef,
      enableNewCategorySettings,
      enableUsageTypes,
      getCategoryForId,
      renderExclusions,
      renderTaxDescription,
      taxItemsEnabled,
    ],
  );

  const getRowClassName = ({ row }) => classNames(classes.row, row.usageTypeRow ? classes.usageTypeRow : null);

  const handleOnAddCategory = () => {
    setShowUpsertCategoryDialog(null);
  };

  const handleRowSingleClick = useCallback(
    (params, event) => {
      if (!event?.defaultPrevented) {
        const category = getCategoryForId(params.id);
        if (category) {
          setShowUpsertCategoryDialog(category);
        }
      }
    },
    [getCategoryForId],
  );

  return (
    <>
      <SettingsHeader
        title='Categories'
        primaryItem={
          <QButton id='add-category' onClick={handleOnAddCategory} variant='add'>
            Category
          </QButton>
        }
        secondaryItem={
          <SearchBox
            onSearch={(value) => setSearchValue(value != null ? searchValueToRegex(value) : null)}
            collapsable
            autoSearch
            openClassName={classes.searchBoxOpen}
            placeholder='Search category'
          />
        }
        tertiaryItem={
          <>
            <Dump value={taxLineItemsById} id='tax-line-items' />
            <Dump value={taxFormsById} id='tax-forms' />
          </>
        }
      />
      <Paper sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh)', p: 2, width: '100%' }}>
        <Box display='flex' flex='1' height='100%'>
          <DataGridStyle1
            apiRef={apiRef}
            columns={columns}
            defaultGroupingExpansionDepth={-1}
            disableChildrenFiltering
            disableColumnPinning
            disableColumnResize
            disableColumnMenu
            disableColumnSort
            getTreeDataPath={getName}
            getRowClassName={getRowClassName}
            groupingColDef={{
              headerName: 'Name',
              disableColumnMenu: false,
              editable: false,
              filterable: false,
              headerClassName: classes.groupColHeader,
              hideable: false,
              hideDescendantCount: true,
              flex: 1,
              valueFormatter: (_value, row) => {
                const category = getCategoryForId(row.id);
                return category?.name || row.id;
              },
            }}
            hideFooter
            onRowClick={handleRowSingleClick}
            rowReordering={false}
            rows={rows}
            sx={{
              '& .disable-blue-border, & .MuiDataGrid-cell:focus': {
                outline: 'none !important',
                boxShadow: 'none',
              },
              '& .MuiDataGrid-row.Mui-hovered, .MuiDataGrid-row.Mui-selected': {
                '& .CategoryButton': {
                  // show buttons on hover, focus
                  opacity: 1,
                },
              },
            }}
            className={classes.root}
            treeData
          />
        </Box>
      </Paper>
      {showUpsertCategoryDialog !== undefined && (
        <UpsertCategoryDialog
          category={showUpsertCategoryDialog}
          enableNewCategorySettings={enableNewCategorySettings}
          enableTaxItems={taxItemsEnabled}
          enableUsageTypes={enableUsageTypes}
          onClose={() => setShowUpsertCategoryDialog(undefined)}
        />
      )}
    </>
  );
}

export default memo(CategoriesSettings);
